import React, { useCallback, useEffect, useState } from 'react';

const BOARD_SIZE = 18;
const STARTING_SNAKE = [{ x: 8, y: 9 }, { x: 7, y: 9 }, { x: 6, y: 9 }];
const STARTING_FOOD = { x: 13, y: 6 };
const DIRECTIONS = {
  up: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
};

function makeFood(snake) {
  const available = [];
  for (let y = 0; y < BOARD_SIZE; y += 1) {
    for (let x = 0; x < BOARD_SIZE; x += 1) {
      if (!snake.some(segment => segment.x === x && segment.y === y)) available.push({ x, y });
    }
  }
  return available[Math.floor(Math.random() * available.length)];
}

const opposite = { up: 'down', right: 'left', down: 'up', left: 'right' };

export default function NotFound() {
  const [snake, setSnake] = useState(STARTING_SNAKE);
  const [food, setFood] = useState(STARTING_FOOD);
  const [queuedDirection, setQueuedDirection] = useState('right');
  const [status, setStatus] = useState('playing');
  const [score, setScore] = useState(0);

  const reset = useCallback(() => {
    setSnake(STARTING_SNAKE);
    setFood(STARTING_FOOD);
    setQueuedDirection('right');
    setScore(0);
    setStatus('playing');
  }, []);

  const changeDirection = useCallback((nextDirection) => {
    setQueuedDirection(current => (opposite[current] === nextDirection ? current : nextDirection));
    setStatus(current => (current === 'game-over' ? current : 'playing'));
  }, []);

  useEffect(() => {
    const onKeyDown = event => {
      const keys = {
        ArrowUp: 'up', w: 'up',
        ArrowRight: 'right', d: 'right',
        ArrowDown: 'down', s: 'down',
        ArrowLeft: 'left', a: 'left',
      };
      const nextDirection = keys[event.key];
      if (nextDirection) {
        event.preventDefault();
        changeDirection(nextDirection);
      }
      if (event.key === ' ' && status !== 'game-over') setStatus(current => current === 'paused' ? 'playing' : 'paused');
      if (event.key === 'Enter' && status === 'game-over') reset();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [changeDirection, reset, status]);

  useEffect(() => {
    if (status !== 'playing') return undefined;
    const timer = window.setInterval(() => {
      setSnake(currentSnake => {
        const nextHead = {
          x: currentSnake[0].x + DIRECTIONS[queuedDirection].x,
          y: currentSnake[0].y + DIRECTIONS[queuedDirection].y,
        };
        const hitWall = nextHead.x < 0 || nextHead.x >= BOARD_SIZE || nextHead.y < 0 || nextHead.y >= BOARD_SIZE;
        const eating = nextHead.x === food.x && nextHead.y === food.y;
        const bodyToCheck = eating ? currentSnake : currentSnake.slice(0, -1);
        if (hitWall || bodyToCheck.some(segment => segment.x === nextHead.x && segment.y === nextHead.y)) {
          setStatus('game-over');
          return currentSnake;
        }
        const nextSnake = [nextHead, ...currentSnake];
        if (eating) {
          setScore(currentScore => currentScore + 1);
          setFood(makeFood(nextSnake));
        } else nextSnake.pop();
        return nextSnake;
      });
    }, 220);
    return () => window.clearInterval(timer);
  }, [food, queuedDirection, status]);

  const cells = Array.from({ length: BOARD_SIZE * BOARD_SIZE }, (_, index) => {
    const x = index % BOARD_SIZE;
    const y = Math.floor(index / BOARD_SIZE);
    const snakeIndex = snake.findIndex(segment => segment.x === x && segment.y === y);
    const isFood = food.x === x && food.y === y;
    return <span key={`${x}-${y}`} className={`snake-cell${snakeIndex === 0 ? ' is-head' : ''}${snakeIndex > 0 ? ' is-snake' : ''}${isFood ? ' is-food' : ''}`} />;
  });

  return <main className="not-found-page">
    <div className="not-found-shell">
      <section className="not-found-intro" aria-labelledby="not-found-title"><h1 id="not-found-title">404<span>.</span></h1><a className="not-found-home button button-outline" href="/">go to the good stuff</a></section>
      <section className="snake-game" aria-label="Snake game">
        <div className="snake-game-bar"><span></span><span className="snake-score">SCORE <strong>{String(score).padStart(2, '0')}</strong></span></div>
        <div className="snake-board" role="img" aria-label={`Snake game, score ${score}. Use arrow keys or the controls below.`}>{cells}{status !== 'playing' && <div className="snake-overlay"><span>{status === 'paused' ? 'PAUSED' : 'GAME OVER'}</span>{status === 'game-over' && <button type="button" onClick={reset}>Play again ↗</button>}</div>}</div>
        <div className="snake-controls" aria-label="Snake controls">
          <button type="button" aria-label="Move up" onClick={() => changeDirection('up')}>↑</button>
          <div><button type="button" aria-label="Move left" onClick={() => changeDirection('left')}>←</button><button type="button" aria-label="Pause or resume" onClick={() => setStatus(current => current === 'paused' ? 'playing' : current === 'playing' ? 'paused' : 'playing')}>Ⅱ</button><button type="button" aria-label="Move right" onClick={() => changeDirection('right')}>→</button></div>
          <button type="button" aria-label="Move down" onClick={() => changeDirection('down')}>↓</button>
        </div>
        <p className="snake-hint">ARROW KEYS / WASD <span>·</span> SPACE TO PAUSE</p>
      </section>
    </div>
  </main>;
}
